import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { usePendingApprovals } from '../contexts/PendingApprovalsContext';
import Sidebar from './Sidebar';
import LoadingSpinner from './LoadingSpinner';

const PendingApprovalsPage = () => {
  const navigate = useNavigate();
  const { refreshPendingCount } = usePendingApprovals();
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [clinic, setClinic] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const [editingDurationId, setEditingDurationId] = useState(null);
  const [durationInfo, setDurationInfo] = useState({ selected: 30, max: 240 });
  const [durationStep, setDurationStep] = useState(30);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const toastTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  const showToast = (message, type = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, type });
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 4000);
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (clinic) {
      fetchPendingAppointments();
      fetchClinicSettings();
    }
  }, [clinic]);

  const fetchClinicSettings = async () => {
    try {
      const { data } = await supabase
        .from('settings_clinic_settings')
        .select('appointment_duration_minutes')
        .eq('clinic_id', clinic.id)
        .single();
      if (data?.appointment_duration_minutes) {
        setDurationStep(data.appointment_duration_minutes);
      }
    } catch {}
  };

  const init = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate('/login'); return; }

      const { data: clinicData } = await supabase
        .from('clinics')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (!clinicData) {
        // Try as vet
        const { data: vet } = await supabase
          .from('vets')
          .select('clinic_id')
          .eq('user_id', user.id)
          .single();
        if (vet) {
          const { data: c } = await supabase
            .from('clinics')
            .select('*')
            .eq('id', vet.clinic_id)
            .single();
          setClinic(c || null);
        }
      } else {
        setClinic(clinicData);
      }
    } catch {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingAppointments = async () => {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select(`
          id,
          appointment_date,
          duration_minutes,
          notes,
          status,
          client_first_name,
          client_last_name,
          client_pet_name,
          client_phone,
          vet_id,
          vets ( first_name, last_name )
        `)
        .eq('clinic_id', clinic.id)
        .eq('status', 'scheduled')
        .is('deleted_at', null)
        .order('appointment_date', { ascending: true });

      if (error) throw error;
      setAppointments(data || []);
    } catch (err) {
      if (import.meta.env.DEV) console.error('Error fetching pending appointments:', err);
      setAppointments([]);
    }
  };

  const updateStatus = async (appointmentId, newStatus) => {
    setActionLoading(appointmentId);
    try {
      const { error } = await supabase
        .from('appointments')
        .update({ status: newStatus })
        .eq('id', appointmentId);

      if (error) throw error;

      showToast(
        newStatus === 'confirmed'
          ? 'Το ραντεβού εγκρίθηκε επιτυχώς.'
          : 'Το ραντεβού απορρίφθηκε.',
        'success'
      );

      // Remove from local list immediately
      setAppointments(prev => prev.filter(a => a.id !== appointmentId));
      refreshPendingCount();
    } catch (err) {
      if (import.meta.env.DEV) console.error('Failed to update status:', err);
      showToast('Σφάλμα κατά την ενημέρωση.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const toggleNotes = (id) => {
    setExpandedNotes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const startEditingDuration = async (apt) => {
    setActionLoading(apt.id);
    try {
      const dateStr = new Date(apt.appointment_date).toISOString().split('T')[0];
      const { data: dayAppointments } = await supabase
        .from('appointments')
        .select('appointment_date, duration_minutes')
        .eq('clinic_id', clinic.id)
        .eq('vet_id', apt.vet_id)
        .gte('appointment_date', `${dateStr}T00:00:00`)
        .lte('appointment_date', `${dateStr}T23:59:59`)
        .neq('id', apt.id)
        .neq('status', 'cancelled')
        .is('deleted_at', null)
        .order('appointment_date', { ascending: true });

      const aptStart = new Date(apt.appointment_date);
      const nextApt = (dayAppointments || []).find(a => new Date(a.appointment_date) > aptStart);

      let maxDuration;
      if (nextApt) {
        maxDuration = Math.floor((new Date(nextApt.appointment_date) - aptStart) / 60000);
      } else {
        maxDuration = 240; // 4 hours cap if no next appointment
      }

      // Ensure max is at least the current duration
      maxDuration = Math.max(maxDuration, apt.duration_minutes || durationStep);

      setDurationInfo({ selected: apt.duration_minutes || durationStep, max: maxDuration });
      setEditingDurationId(apt.id);
    } catch (err) {
      if (import.meta.env.DEV) console.error('Failed to check availability:', err);
      showToast('Σφάλμα κατά τον έλεγχο διαθεσιμότητας.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const cancelEditingDuration = () => {
    setEditingDurationId(null);
    setDurationInfo({ selected: 30, max: 240 });
  };

  const saveDuration = async (appointmentId) => {
    setActionLoading(appointmentId);
    try {
      const { error } = await supabase
        .from('appointments')
        .update({ duration_minutes: durationInfo.selected })
        .eq('id', appointmentId);

      if (error) throw error;

      setAppointments(prev =>
        prev.map(a => a.id === appointmentId ? { ...a, duration_minutes: durationInfo.selected } : a)
      );
      showToast('Η διάρκεια ενημερώθηκε επιτυχώς.');
      cancelEditingDuration();
    } catch (err) {
      if (import.meta.env.DEV) console.error('Failed to update duration:', err);
      showToast('Σφάλμα κατά την αλλαγή διάρκειας.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const getDurationOptions = () => {
    const step = 15;
    const options = [];
    for (let m = step; m <= durationInfo.max; m += step) {
      options.push(m);
    }
    // Ensure current duration is included even if it doesn't align with step
    if (!options.includes(durationInfo.selected)) {
      options.push(durationInfo.selected);
      options.sort((a, b) => a - b);
    }
    return options;
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} λεπτά`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}ω ${m}λ` : `${h} ώρ${h > 1 ? 'ες' : 'α'}`;
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('el-GR', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#1A202C' }}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" style={{ backgroundColor: '#1A202C', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, background: 'radial-gradient(800px 800px at 10% -10%, rgba(16,185,129,0.12), transparent), radial-gradient(800px 800px at 110% 10%, rgba(59,130,246,0.08), transparent)' }} className="pointer-events-none" />
      <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />

      <div className="flex-1 p-4 lg:p-8 overflow-auto z-10">
        {/* Header Card */}
        <div className="mb-6 lg:mb-8 rounded-2xl border p-5 lg:p-6 shadow-lg flex flex-col sm:flex-row sm:items-center justify-between gap-4" style={{ backgroundColor: '#111827', borderColor: '#374151' }}>
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <div className="p-2 bg-amber-500/10 rounded-xl border border-amber-500/20">
                <svg className="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              Εκκρεμείς Εγκρίσεις
            </h2>
            <p className="text-gray-400 text-sm mt-1">Ραντεβού που περιμένουν τη δική σας επιβεβαίωση</p>
          </div>
          <div className="flex items-center">
            <span
              className={`px-4 py-1.5 text-sm font-bold rounded-full border shadow-sm ${
                appointments.length > 0 
                  ? 'bg-red-500/10 text-red-400 border-red-500/20' 
                  : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
              }`}
            >
              {appointments.length} {appointments.length === 1 ? 'Εκκρεμότητα' : 'Εκκρεμότητες'}
            </span>
          </div>
        </div>

        {appointments.length === 0 ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center py-24 text-gray-500 rounded-2xl border bg-[#111827] border-gray-700 shadow-xl">
            <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mb-5 border border-emerald-500/20">
              <svg className="w-10 h-10 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-xl font-bold text-white mb-2">Όλα είναι τακτοποιημένα!</p>
            <p className="text-gray-400">Δεν υπάρχουν νέα ραντεβού προς έγκριση αυτή τη στιγμή.</p>
          </div>
        ) : (
          /* Table */
          <div className="rounded-2xl border shadow-2xl overflow-hidden" style={{ backgroundColor: '#111827', borderColor: '#374151' }}>
            
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b" style={{ backgroundColor: 'rgba(31,41,55,0.5)', borderColor: '#374151' }}>
                    <th className="text-left px-5 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Κατοικίδιο</th>
                    <th className="text-left px-5 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Πελάτης</th>
                    <th className="text-left px-5 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Τηλέφωνο</th>
                    <th className="text-left px-5 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Ημ/νία & Ώρα</th>
                    <th className="text-left px-5 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Κτηνίατρος</th>
                    <th className="text-left px-5 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Σημειώσεις</th>
                    <th className="text-right px-5 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Ενέργειες</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: '#374151' }}>
                  {appointments.map((apt) => (
                    <tr key={apt.id} className="hover:bg-[#1F2937]/50 transition-colors">
                      <td className="px-5 py-4 text-white font-medium whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center border border-blue-500/30">
                            <span className="text-blue-400 text-xs font-bold">{apt.client_pet_name ? apt.client_pet_name.charAt(0).toUpperCase() : '🐾'}</span>
                          </div>
                          {apt.client_pet_name || '—'}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-gray-300">
                        {[apt.client_first_name, apt.client_last_name].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className="px-5 py-4 text-gray-300 font-mono text-sm">
                        {apt.client_phone || '—'}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-medium">{formatDate(apt.appointment_date)}</span>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded border border-blue-500/20">{formatTime(apt.appointment_date)}</span>
                            <span className="text-xs text-gray-400">{formatDuration(apt.duration_minutes || durationStep)}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-gray-300">
                        {apt.vets ? `${apt.vets.first_name} ${apt.vets.last_name}` : '—'}
                      </td>
                      <td className="px-5 py-4 text-gray-300 max-w-[220px]">
                        {apt.notes ? (
                          <div>
                            <p className={`text-sm text-gray-400 italic ${expandedNotes.has(apt.id) ? '' : 'line-clamp-2'}`}>
                              "{apt.notes}"
                            </p>
                            {apt.notes.length > 60 && (
                              <button
                                onClick={() => toggleNotes(apt.id)}
                                className="text-[10px] uppercase tracking-wider font-bold text-emerald-500 hover:text-emerald-400 mt-1 transition-colors"
                              >
                                {expandedNotes.has(apt.id) ? 'Λιγότερα' : 'Περισσότερα...'}
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-600">—</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-right">
                        {editingDurationId === apt.id ? (
                          <div className="flex items-center justify-end gap-3 bg-[#1F2937] p-1.5 rounded-lg border border-gray-700 w-fit ml-auto">
                            <div className="relative">
                              <select
                                value={durationInfo.selected}
                                onChange={(e) => setDurationInfo(prev => ({ ...prev, selected: Number(e.target.value) }))}
                                className="appearance-none pl-3 pr-8 py-1.5 rounded-md text-sm font-medium text-white border focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all cursor-pointer"
                                style={{ backgroundColor: '#111827', borderColor: '#4A5568' }}
                              >
                                {getDurationOptions().map(m => (
                                  <option key={m} value={m}>{formatDuration(m)}</option>
                                ))}
                              </select>
                              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                              </div>
                            </div>
                            <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">MAX: {formatDuration(durationInfo.max)}</span>
                            
                            <div className="flex items-center gap-1 border-l border-gray-600 pl-2">
                              <button
                                onClick={() => saveDuration(apt.id)}
                                disabled={actionLoading === apt.id}
                                className="p-1.5 rounded-md text-emerald-500 hover:bg-emerald-500/20 hover:text-emerald-400 transition-colors"
                                title="Αποθήκευση Διάρκειας"
                              >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                              </button>
                              <button
                                onClick={cancelEditingDuration}
                                className="p-1.5 rounded-md text-gray-400 hover:bg-red-500/20 hover:text-red-400 transition-colors"
                                title="Ακύρωση"
                              >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-2">
                            {actionLoading === apt.id ? (
                              <div className="animate-spin h-5 w-5 border-2 border-emerald-500 border-t-transparent rounded-full mr-4"></div>
                            ) : (
                              <>
                                <button
                                  onClick={() => startEditingDuration(apt)}
                                  className="p-2 rounded-lg text-gray-400 hover:bg-blue-500/10 hover:text-blue-400 transition-colors"
                                  title="Αλλαγή Διάρκειας"
                                >
                                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                </button>
                                <div className="w-px h-5 bg-gray-700 mx-1"></div>
                                <button
                                  onClick={() => updateStatus(apt.id, 'confirmed')}
                                  className="p-2 rounded-lg text-gray-400 hover:bg-emerald-500/10 hover:text-emerald-400 transition-colors"
                                  title="Έγκριση Ραντεβού"
                                >
                                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                </button>
                                <button
                                  onClick={() => updateStatus(apt.id, 'cancelled')}
                                  className="p-2 rounded-lg text-gray-400 hover:bg-red-500/10 hover:text-red-400 transition-colors"
                                  title="Απόρριψη Ραντεβού"
                                >
                                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-800">
              {appointments.map((apt) => (
                <div key={apt.id} className="p-5 space-y-4 bg-[#111827]">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center border border-blue-500/30">
                          <span className="text-blue-400 text-[10px] font-bold">{apt.client_pet_name ? apt.client_pet_name.charAt(0).toUpperCase() : '🐾'}</span>
                        </div>
                        <p className="text-white font-bold text-lg">{apt.client_pet_name || 'Ραντεβού'}</p>
                      </div>
                      <p className="text-sm text-gray-300">
                        👤 {[apt.client_first_name, apt.client_last_name].filter(Boolean).join(' ') || '—'}
                      </p>
                      {apt.client_phone && (
                        <p className="text-sm text-gray-400 mt-1 font-mono">📞 {apt.client_phone}</p>
                      )}
                    </div>
                    <div className="text-right flex flex-col items-end">
                      <span className="bg-[#1F2937] border border-gray-700 px-2.5 py-1 rounded-md text-sm font-medium text-white mb-1">{formatDate(apt.appointment_date)}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-blue-400">{formatTime(apt.appointment_date)}</span>
                        <span className="text-xs text-gray-500">{formatDuration(apt.duration_minutes || durationStep)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {apt.vets && (
                    <div className="flex items-center gap-2 bg-[#1F2937] p-2.5 rounded-lg border border-gray-700">
                      <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                      <p className="text-sm text-gray-300">Ιατρός: <span className="font-medium text-white">{apt.vets.first_name} {apt.vets.last_name}</span></p>
                    </div>
                  )}

                  {apt.notes && (
                    <div className="rounded-lg p-3 bg-gray-800/50 border border-gray-700">
                      <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">Σημειωσεις Πελατη</p>
                      <p className={`text-sm text-gray-300 italic ${expandedNotes.has(apt.id) ? '' : 'line-clamp-2'}`}>
                        "{apt.notes}"
                      </p>
                      {apt.notes.length > 80 && (
                        <button
                          onClick={() => toggleNotes(apt.id)}
                          className="text-xs font-bold text-emerald-400 hover:text-emerald-300 mt-2 transition-colors"
                        >
                          {expandedNotes.has(apt.id) ? 'Λιγότερα' : 'Διαβάστε Περισσότερα...'}
                        </button>
                      )}
                    </div>
                  )}

                  {editingDurationId === apt.id ? (
                    <div className="flex flex-col gap-3 p-4 bg-[#1F2937] rounded-xl border border-gray-700">
                      <label className="text-xs text-gray-400 font-medium">Επιλογή Νέας Διάρκειας</label>
                      <div className="flex items-center gap-2">
                        <select
                          value={durationInfo.selected}
                          onChange={(e) => setDurationInfo(prev => ({ ...prev, selected: Number(e.target.value) }))}
                          className="flex-1 px-3 py-2.5 rounded-lg text-sm font-medium text-white border bg-[#111827] focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                          style={{ borderColor: '#4A5568' }}
                        >
                          {getDurationOptions().map(m => (
                            <option key={m} value={m}>{formatDuration(m)}</option>
                          ))}
                        </select>
                        <span className="text-xs text-gray-500 whitespace-nowrap bg-[#111827] px-2 py-2 rounded-lg border border-gray-700">Max: {formatDuration(durationInfo.max)}</span>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <button onClick={cancelEditingDuration} className="flex-1 py-2.5 rounded-lg font-medium text-gray-300 bg-[#111827] border border-gray-600 transition-colors">
                          Ακύρωση
                        </button>
                        <button onClick={() => saveDuration(apt.id)} disabled={actionLoading === apt.id} className="flex-1 py-2.5 rounded-lg font-medium text-white bg-emerald-600 transition-colors disabled:opacity-50">
                          Αποθήκευση
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => startEditingDuration(apt)}
                        disabled={actionLoading === apt.id || editingDurationId === apt.id}
                        className="flex-1 flex flex-col items-center justify-center gap-1 py-3 rounded-xl border border-gray-700 bg-[#1F2937] text-gray-300 hover:bg-gray-700 transition-all disabled:opacity-50"
                      >
                        <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <span className="text-xs font-medium">Διάρκεια</span>
                      </button>
                      <button
                        onClick={() => updateStatus(apt.id, 'cancelled')}
                        disabled={actionLoading === apt.id}
                        className="flex-1 flex flex-col items-center justify-center gap-1 py-3 rounded-xl border border-red-500/20 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all disabled:opacity-50"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        <span className="text-xs font-medium">Απόρριψη</span>
                      </button>
                      <button
                        onClick={() => updateStatus(apt.id, 'confirmed')}
                        disabled={actionLoading === apt.id}
                        className="flex-1 flex flex-col items-center justify-center gap-1 py-3 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50 disabled:scale-100"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                        <span className="text-xs font-bold">Έγκριση</span>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-20"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Glassmorphism Toast Notification */}
      {toast.show && (
        <div className="fixed top-4 right-4 left-4 sm:left-auto z-[60]">
          <div
            className="flex items-center px-5 py-4 rounded-xl border max-w-sm ml-auto transition-all duration-300 shadow-2xl"
            style={{
              background: 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(31,41,55,0.95) 100%)',
              backdropFilter: 'blur(10px)',
              borderColor: toast.type === 'success' ? '#10B981' : '#EF4444',
            }}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${toast.type === 'success' ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
              <svg className={`w-5 h-5 ${toast.type === 'success' ? 'text-emerald-400' : 'text-red-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={toast.type === 'success' ? "M5 13l4 4L19 7" : "M6 18L18 6M6 6l12 12"} />
              </svg>
            </div>
            <div className="text-white font-medium flex-1 text-sm leading-snug">{toast.message}</div>
            <button
              onClick={() => setToast({ show: false, message: '', type: 'success' })}
              className="ml-4 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingApprovalsPage;